package com.speedsoftware.nibbler.ui.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.SessionDto
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import kotlinx.coroutines.launch

/** Profile card listing the devices logged in to this account, with a button to end each of them. */
@Composable
fun SessionsSection(appState: AppState) {
    val scope = rememberCoroutineScope()
    var sessions by remember { mutableStateOf<List<SessionDto>?>(null) }
    var message by remember { mutableStateOf<String?>(null) }
    var isBusy by remember { mutableStateOf(false) }

    fun load() {
        scope.launch {
            appState.apiClient.getSessions()
                .onSuccess { sessions = it; message = null }
                .onFailure { message = it.message ?: "Could not load your sessions." }
        }
    }

    LaunchedEffect(Unit) { load() }

    Column(
        modifier = Modifier
            .widthIn(max = 560.dp)
            .fillMaxWidth()
            .background(NibblerColors.SurfaceContainer, RoundedCornerShape(8.dp))
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(8.dp))
            .padding(20.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text("Active sessions", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = NibblerColors.OnSurface)
        Text(
            "Browsers and apps that are logged in to your account. Log out any you do not recognise.",
            fontSize = 13.sp,
            color = NibblerColors.OnSurfaceVariant
        )

        val list = sessions
        when {
            list == null && message == null -> Text("Loading...", fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant)
            list != null -> {
                list.forEach { session ->
                    SessionRow(
                        session = session,
                        enabled = !isBusy,
                        onRevoke = {
                            isBusy = true
                            scope.launch {
                                appState.apiClient.revokeSession(session.id)
                                    .onFailure { message = it.message ?: "Could not log that session out." }
                                load()
                                isBusy = false
                            }
                        }
                    )
                }
                if (list.count { !it.current } > 0) {
                    OutlinedButton(
                        enabled = !isBusy,
                        onClick = {
                            isBusy = true
                            scope.launch {
                                appState.apiClient.revokeOtherSessions()
                                    .onFailure { message = it.message ?: "Could not log the other sessions out." }
                                load()
                                isBusy = false
                            }
                        },
                        shape = RoundedCornerShape(8.dp),
                        border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant)
                    ) { Text("Log out all other devices", color = NibblerColors.Error) }
                }
            }
        }
        message?.let { Text(it, fontSize = 12.sp, color = NibblerColors.Error) }
    }
}

@Composable
private fun SessionRow(session: SessionDto, enabled: Boolean, onRevoke: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(NibblerColors.SurfaceContainerLow, RoundedCornerShape(8.dp))
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(8.dp))
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(describeDevice(session.userAgent), fontWeight = FontWeight.Bold, fontSize = 14.sp, color = NibblerColors.OnSurface)
                if (session.current) {
                    Text(
                        "This device",
                        fontFamily = FontFamily.SansSerif,
                        fontWeight = FontWeight.Bold,
                        fontSize = 9.sp,
                        color = NibblerColors.BauhausYellow,
                        modifier = Modifier
                            .border(1.dp, NibblerColors.BauhausYellow, RoundedCornerShape(8.dp))
                            .padding(horizontal = 6.dp, vertical = 2.dp)
                    )
                }
            }
            Text(
                "Signed in ${formatAgo(session.createdAgoSeconds)} · last active ${formatAgo(session.lastSeenAgoSeconds)}",
                fontSize = 12.sp,
                color = NibblerColors.OnSurfaceVariant
            )
        }
        if (!session.current) {
            OutlinedButton(
                enabled = enabled,
                onClick = onRevoke,
                shape = RoundedCornerShape(8.dp),
                border = androidx.compose.foundation.BorderStroke(1.dp, NibblerColors.OutlineVariant)
            ) { Text("Log out", fontSize = 12.sp, color = NibblerColors.OnSurface) }
        }
    }
}
